"use client";

import Link from "next/link";
import Image from "next/image";
import { ArrowRight } from "lucide-react";
import CardCarousel from "./CardCarousel";
import { getLocalizedPath } from "@/lib/slugs";

export interface BlogPostItem {
    id: string | number;
    link: string;
    image: string;
    title: string;
    description: string;
    category: string;
    readTime: string;
}

export interface BlogSectionDict {
    common?: { general?: string };
    home?: {
        blog?: {
            title?: string;
            description?: string;
            see_all?: string;
            read_time?: string;
            no_posts?: string;
        };
    };
}

export default function BlogSectionCarousel({
    posts,
    dict,
    lang = "tr",
}: {
    posts: BlogPostItem[];
    dict?: BlogSectionDict;
    lang?: string;
}) {
    const seeAllLabel = dict?.home?.blog?.see_all || "See All Posts";

    const card = (post: BlogPostItem) => (
        <Link href={post.link} className="group block h-full rounded-2xl focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#00AEEF]/40 focus-visible:ring-offset-4 focus-visible:ring-offset-gray-50">
            {/* Image */}
            <div className="relative mb-4 aspect-[16/10] overflow-hidden rounded-2xl shadow-sm ring-1 ring-black/5">
                <Image
                    src={post.image}
                    alt={post.title}
                    fill
                    className="object-cover transition-transform duration-700 group-hover:scale-105"
                    sizes="(max-width: 640px) 82vw, (max-width: 1024px) 58vw, 33vw"
                />
            </div>

            {/* Meta */}
            <div className="mb-2.5 flex items-center gap-3">
                <span className="bg-[#E0F4FB] text-[#0f4c81] text-[11px] font-semibold px-3 py-1 rounded-full">
                    {post.category || dict?.common?.general}
                </span>
                <span className="text-gray-600 text-[12px] font-medium">
                    {post.readTime} {dict?.home?.blog?.read_time}
                </span>
            </div>

            {/* Content */}
            <h3 className="mb-2 text-lg font-bold leading-snug text-[#051e3c] transition-colors group-hover:text-[#0f4c81]">
                {post.title}
            </h3>
            <p className="line-clamp-3 text-[14px] leading-relaxed text-gray-600">
                {post.description}
            </p>
        </Link>
    );

    return (
        <section className="py-16 lg:py-20 bg-gray-50">
            <div className="container-custom">
                <div className="mb-8 flex flex-col gap-5 md:mb-12 md:flex-row md:items-end md:justify-between">
                    <div className="max-w-2xl">
                        <h2 className="text-3xl font-bold tracking-[-0.03em] text-[#051e3c] md:text-[2.5rem] md:leading-[1.1]">{dict?.home?.blog?.title}</h2>
                        <p className="mt-4 text-[16px] leading-relaxed text-gray-600">
                            {dict?.home?.blog?.description}
                        </p>
                    </div>
                    <Link
                        href={getLocalizedPath("blog", lang as "tr" | "en" | "ar")}
                        className="group inline-flex shrink-0 items-center justify-center gap-2 self-start rounded-full bg-[#0f4c81] px-5 py-2.5 text-[14px] font-semibold text-white shadow-sm transition-colors hover:bg-[#0a355c] md:self-auto"
                    >
                        {seeAllLabel}
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                    </Link>
                </div>

                {posts.length === 0 ? (
                    <div className="py-10 text-center text-gray-600">
                        {dict?.home?.blog?.no_posts}
                    </div>
                ) : (
                    <>
                        {/* Desktop: grid */}
                        <div className="hidden lg:grid grid-cols-3 gap-6">
                            {posts.map((post) => (
                                <div key={post.id}>{card(post)}</div>
                            ))}
                        </div>

                        {/* Mobile / tablet: carousel */}
                        <div className="lg:hidden">
                            <CardCarousel
                                ariaLabel="post"
                                itemClassName="w-[82%] sm:w-[58%] md:w-[44%]"
                                items={posts.map((post) => card(post))}
                            />
                        </div>
                    </>
                )}
            </div>
        </section>
    );
}
